import { isDefined } from '@sgme/fp';
import type { IntlFormatters } from 'react-intl';

import { isNotDefined } from '@/components/sg-design-system/utils/index.ts';
import { forwardAccumulatorApi } from '@/store/forwardAccumulator/api/index.ts';
import { createForwardAccumulatorRfsRequestParams } from '@/store/forwardAccumulator/api/multiPass/requestForwardAccumulatorQuote.ts';
import {
  getForwardAccumulatorQuote,
  getForwardAccumulatorRfsId,
  getForwardAccumulatorRfsPremiumData,
  getForwardAccumulatorRfsQuoteId,
} from '@/store/forwardAccumulator/selectors.ts';
import { forwardAccumulatorSlice } from '@/store/forwardAccumulator/slice.ts';
import type { AppState } from '@/store/store.ts';
import { createAppAsyncThunk } from '@/store/storeMethods.ts';
import { getSignalRConnectionId } from '@/store/streaming/slice.ts';
import { selectActiveTabId } from '@/store/tabs/slice.ts';
import { selectTileClientId, selectTilesOfTab } from '@/store/tiles/selectors.ts';
import { formatStringDateTime } from '@/utils/date.ts';
import { getNewRfsId } from '@/utils/rfs/getNewRfsId.ts';

export const cancelAllForwardAccumulatorRfsThunk = createAppAsyncThunk<void, void>(
  'global/cancelRfs',
  async (_, api) => {
    const state: AppState = api.getState();
    const currentTabId = selectActiveTabId(state);
    const tiles = selectTilesOfTab(state, currentTabId);

    const cancellationActions = tiles
      .map(({ tileId }) => {
        const accumulator = forwardAccumulatorSlice.selectors.getForwardAccumulator(state, tileId);
        const rfsId = isDefined(accumulator) ? getForwardAccumulatorRfsId(accumulator) : undefined;
        if (isDefined(accumulator) && isDefined(rfsId)) {
          return forwardAccumulatorApi.endpoints.cancelForwardAccumulatorRfs.initiate({
            tileId: tileId,
            rfsId: rfsId,
            removeQuote: true,
          });
        }
        return undefined;
      })
      .filter(isDefined);
    await Promise.all(cancellationActions.map((cancelAction) => api.dispatch(cancelAction)));
  },
);

interface ForwardAccumulatorRfsThunkParams {
  tileId: string;
  forceTradeable: boolean;
  skipCreditCheck?: boolean;
}

export const requestForwardAccumulatorRfsThunk = createAppAsyncThunk<void, ForwardAccumulatorRfsThunkParams>(
  'forwardAccumulator/requestRfs',
  async (params, { dispatch, getState }) => {
    const { tileId, forceTradeable, skipCreditCheck = false } = params;

    const accumulatorBeforeCancel = getState().forwardAccumulator[tileId];
    if (isNotDefined(accumulatorBeforeCancel)) {
      console.warn('Could not create new rfs, tile is undefined');
      return;
    }

    let rfsId = getForwardAccumulatorRfsId(accumulatorBeforeCancel);

    if (isDefined(rfsId)) {
      await dispatch(
        forwardAccumulatorApi.endpoints.cancelForwardAccumulatorRfs.initiate({
          tileId,
          rfsId,
        }),
      );
    }

    const accumulatorAfterCancel = getState().forwardAccumulator[tileId];
    if (isNotDefined(accumulatorAfterCancel)) {
      console.warn('Could not create new rfs, tile is undefined');
      return;
    }

    rfsId = getForwardAccumulatorRfsId(accumulatorAfterCancel);
    const newRfsId = isDefined(rfsId) ? rfsId : getNewRfsId();
    const clientId = selectTileClientId(getState(), tileId);
    const signalRConnectionId = getSignalRConnectionId(getState());

    const rfsRequestParams = createForwardAccumulatorRfsRequestParams(
      accumulatorAfterCancel,
      tileId,
      newRfsId,
      clientId,
      signalRConnectionId,
      forceTradeable,
      skipCreditCheck,
    );

    if (isDefined(rfsRequestParams)) {
      await dispatch(
        forwardAccumulatorApi.endpoints.requestForwardAccumulatorRfs.initiate(rfsRequestParams, {
          fixedCacheKey: newRfsId,
        }),
      );
    }
  },
);

export const resetForwardAccumulatorThunk = createAppAsyncThunk<void, { tileId: string; forcedCurrencyPair?: string }>(
  'forwardAccumulator/reset',
  async ({ tileId, forcedCurrencyPair }, { dispatch, getState }) => {
    const accumulator = forwardAccumulatorSlice.selectors.getForwardAccumulator(getState(), tileId);

    if (isNotDefined(accumulator)) {
      return;
    }

    const rfsId = getForwardAccumulatorRfsId(accumulator);

    dispatch(forwardAccumulatorSlice.actions.forwardAccumulatorReset({ tileId }));
    if (isDefined(rfsId)) {
      dispatch(forwardAccumulatorApi.endpoints.cancelForwardAccumulatorRfs.initiate({ tileId, rfsId }));
    }

    await dispatch(
      forwardAccumulatorApi.endpoints.checkForwardAccumulator.initiate({
        tileId: tileId,
        properties: {
          currencyPair: forcedCurrencyPair ?? accumulator.properties.currencyPair,
        },
      }),
    );
  },
);

export const executeForwardAccumulatorThunk = createAppAsyncThunk<void, { tileId: string; userEmail: string }>(
  'forwardAccumulator/execute',
  async ({ tileId, userEmail }, { dispatch, getState }) => {
    const accumulator = forwardAccumulatorSlice.selectors.getForwardAccumulator(getState(), tileId);
    const currentState = getState();

    if (isNotDefined(accumulator)) {
      return;
    }

    const preferedEmails = currentState.userPreferences.splitNotifications
      ? currentState.userPreferences.splitNotificationsEmailsOptions
      : currentState.userPreferences.emails;

    const notificationEmail = preferedEmails.length > 0 ? preferedEmails.join(';') : userEmail;

    const { notional, notionalCurrencyIndex, expiryDate, expiryDateTenor, firstFixingDate, currencyPair, strike } =
      accumulator.properties;
    const clientId = selectTileClientId(currentState, tileId);

    const rfsId = getForwardAccumulatorRfsId(accumulator);
    const quote = getForwardAccumulatorQuote(accumulator);
    const quoteId = getForwardAccumulatorRfsQuoteId(accumulator);
    const [currency1, currency2] = isDefined(currencyPair) ? currencyPair.split('/') : ['', ''];

    const premiumData = getForwardAccumulatorRfsPremiumData(quote);
    const premiumAmount = premiumData?.premiumAmount;

    const premiumAmountString = isDefined(premiumAmount)
      ? premiumAmount[accumulator.properties.premiumTypeCurrencyIndex - 1]
      : undefined;

    if (
      isNotDefined(userEmail) ||
      isNotDefined(clientId) ||
      isNotDefined(quoteId) ||
      isNotDefined(rfsId) ||
      isNotDefined(currency1) ||
      isNotDefined(currency2) ||
      isNotDefined(notional) ||
      isNotDefined(notionalCurrencyIndex) ||
      isNotDefined(strike) ||
      isNotDefined(expiryDate) ||
      isNotDefined(firstFixingDate) ||
      isNotDefined(quote)
    ) {
      return;
    }

    dispatch(
      forwardAccumulatorApi.endpoints.executeForwardAccumulatorRfs.initiate(
        {
          notificationEmail,
          tileId,
          quoteId,
          rfsId,
          accountId: clientId,
          currency1,
          currency2,
          notional,
          notionalCurrencyIndex,
          userLocalDateTime: formatStringDateTime(new Date()),
          strike: strike,
          expiry: {
            date: expiryDate,
            tenor: expiryDateTenor,
          },

          firstFixing: {
            date: firstFixingDate,
            tenor: accumulator.properties.firstFixingTenor,
          },
          fixingFrequency: accumulator.properties.fixingFrequency,
          numberOfFixings: accumulator.properties.numberOfFixings,
          premium: String(premiumAmountString),
          premiumCurrencyIndex: accumulator.properties.premiumTypeCurrencyIndex,
          amountMode: accumulator.properties.amountMode,
          accuType: accumulator.properties.accuType,
          clientsWay: accumulator.properties.clientsWay,

          akoTrigger: accumulator.properties.akoTrigger,
          ekiTrigger: accumulator.properties.ekiTrigger,
          step: accumulator.properties.step,

          lastQuoteReceivedByFrontTimestamp: quote.frontTimestamp,
          lastQuoteFromBackTimestamp: quote.backTimestamp,
        },
        { fixedCacheKey: rfsId },
      ),
    );
  },
);

export const copyToClipBoardForwardAccumulatorThunk = createAppAsyncThunk<
  void,
  { tileId: string; formatNumber: IntlFormatters['formatNumber'] }
>('ForwardAccumulator/copyToClipboard', async ({ tileId, formatNumber }, { getState }) => {
  const formatNumberWithDecimal = (value: number) =>
    formatNumber(value, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  // TODO check ok after forward accumulator is done

  const accumulator = forwardAccumulatorSlice.selectors.getForwardAccumulator(getState(), tileId);
  if (isNotDefined(accumulator)) {
    return;
  }
  const accumulatorQuoteData = getForwardAccumulatorQuote(accumulator);
  const {
    amountMode: amountModeFromStore,
    currencyPair,
    clientsWay,
    fixingFrequency,
    fixingReference1,
    leverage,
    leverageAmount,
    firstFixingDate,
    firstFixingTenor,
    hedgeType,
    expiryDate,
    expiryDateTenor,
    strike,
    premiumTypeCurrencyIndex,
    numberOfFixings,
    notional,
    notionalCurrencyIndex,
    // targetProfitType,
    // target,
  } = accumulator?.properties || {};

  let premium: number | undefined;
  let premiumPercent: number | undefined;
  const currency = currencyPair?.split('/')[notionalCurrencyIndex - 1];

  if (accumulatorQuoteData) {
    const { premiumCcy1, premiumCcy2, premiumPctCcy1, premiumPctCcy2 } = accumulatorQuoteData.premiumData;
    premium = [premiumCcy1, premiumCcy2][premiumTypeCurrencyIndex - 1]?.ask;
    premiumPercent = [premiumPctCcy1, premiumPctCcy2][premiumTypeCurrencyIndex - 1];
  }

  const underlyingField = `Underlying: ${currencyPair} (${clientsWay} ${currency})\n`;
  const fixingFrequencyField = `Fixing Frequency: ${numberOfFixings} ${fixingFrequency} (${fixingReference1})\n`;
  const settlementFrequencyField = `Settlement Frequency: ${numberOfFixings} ${fixingFrequency}\n`;
  const leverageField = `Leverage: ${leverage}\n`;

  const amountMode = amountModeFromStore === 'Total' || firstFixingDate === expiryDate ? 'Total Amount' : 'Per Fixing';
  const notionalField = `Notional: ${formatNumberWithDecimal(Number(notional))} ${currency} (${amountMode})\n`;
  const leveragedNotionalField = `Leveraged Notional: ${formatNumberWithDecimal(Number(leverageAmount))} ${currency}\n`;

  const firstFixingField = `First Fixing: ${firstFixingDate} ${firstFixingTenor ? `(${firstFixingTenor})` : ''}\n`;
  const lastDateField =
    expiryDate !== null ? `Last Date: ${expiryDate} ${expiryDateTenor ? `(${expiryDateTenor})` : ''}\n` : '';
  const strikeField = strike !== null ? `Strike: ${strike}\n` : '';

  const hedge = accumulatorQuoteData?.hedge;

  const spotRef = hedgeType === 'Spot' ? `Spot Ref: ${accumulatorQuoteData ? `${hedge?.rateMid}` : ''}\n` : '';

  const clientPaysOrReceives = premium ? (premium >= 0 ? 'pays' : 'receives') : '';

  const clientPremium = accumulatorQuoteData
    ? `Client ${clientPaysOrReceives} ${formatNumberWithDecimal(Math.abs(Number(premium)))} ${currency} (${premiumPercent}%)`
    : '';
  const premiumField = clientPremium ? `Premium: ${clientPremium}` : '';

  const summary =
    'Product: FWAC\n' +
    `${underlyingField}` +
    `${fixingFrequencyField}` +
    `${settlementFrequencyField}` +
    `${leverageField}` +
    `${notionalField}` +
    `${leveragedNotionalField}` +
    `${firstFixingField}` +
    `${lastDateField}` +
    `${spotRef}` +
    `${strikeField}` +
    //`Profit Target: ${target} (${targetProfitType ?? ''})\n` +
    `${premiumField}`;

  await navigator.clipboard.writeText(summary);
});
